import React from 'react';
import { useTranslation } from 'react-i18next';

import { Text } from '../../../../components/Text/Text';
import { Root, Mark, CostGroup, Sep, CostText } from './GenerateButton.styles';

/** Lumio's mark, the same file the top bar and the chat panel use. */
const LUMIO_MARK_SRC = '/assets/lumio/lumio-mark.svg';

export type GenerateButtonProps = {
  /** English copy; translated here, like every other label in the area. */
  label: string;
  /**
   * The accessible name, when the visible label is not enough on its own. Three
   * buttons reading "Generate" are unambiguous on screen — each sits in the
   * section it writes — and identical to a screen reader, which hears them out
   * of that context.
   */
  name?: string;
  /**
   * Credit cost of the action; shown inline once the price table has loaded,
   * omitted (never zero-filled) before that — the same contract the creatives
   * GenerateButton keeps, so cost reads the same way across the studio.
   */
  cost?: number;
  size?: 'sm' | 'md';
  disabled?: boolean;
  onClick: () => void;
  /** Style hook from a host's `.styles.ts`. */
  className?: string;
};

/**
 * The editor's Generate control: a pill with Lumio's mark and a gradient ring.
 *
 * It exists as a component rather than as a styled button per site because there
 * are six of them (the page header, two panel heads, the hero, the brief
 * dialog's confirm, the quiz), and six copies of a gradient is how a design
 * language ends up with six slightly different gradients.
 */
export const GenerateButton = ({
  label,
  name,
  cost,
  size = 'sm',
  disabled = false,
  onClick,
  className,
}: GenerateButtonProps): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <Root
      type="button"
      $size={size}
      disabled={disabled}
      onClick={onClick}
      className={className}
      aria-label={t(name ?? label)}
    >
      <Mark src={LUMIO_MARK_SRC} alt="" aria-hidden="true" $size={size} />
      <Text text={label} tag="span" />
      {cost != null ? (
        <CostGroup>
          <Sep aria-hidden="true">|</Sep>
          <CostText text={cost === 1 ? '{{n}} credit' : '{{n}} credits'} placeholders={{ n: cost }} />
        </CostGroup>
      ) : null}
    </Root>
  );
};

export default GenerateButton;
